import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import extractApiError from '../../utils/apiError';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';

// PR-A — Dış Müşteri Satışı: Müşteri master data yönetimi.
// /api/cari?tip=MUSTERI üzerinden (tek Cari tablosu, design K1). Anayasa M.2:
// Listele + Ekle + Düzenle + Pasifleştir. Aktifleştir backend ucu (PUT'ta `aktif`
// yok) gelene kadar PR-A'da yok — follow-up. M.3: kod immutable (modal disabled).
// M.5: MANAGE_SATIS ile buton gizleme (backend zaten enforce eder).

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const emptyForm = {
  unvan: '', vergi_no: '', yetkili: '', telefon: '', email: '', adres: '',
  vade_gun: 30, kdv_orani: 8, kategori: 'Genel', muhasebe_kodu: '',
};

const inputStyle = {
  width: '100%', padding: '10px', marginBottom: '12px',
  boxSizing: 'border-box', borderRadius: radius.sm, border: `1px solid ${colors.border}`,
  fontSize: '14px',
};
const labelStyle = { display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' };

const MusterilerPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_SATIS, user.effectivePermissions);
  const toast = useToast();

  const [musteriler, setMusteriler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null=yeni, {…}=düzenle
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [arama, setArama] = useUrlState('mus_q', '', { debounceMs: 300 });
  const [pasifGoster, setPasifGoster] = useUrlState('mus_pasif', '0');

  const token = localStorage.getItem('access_token');

  const fetchMusteriler = useCallback(async () => {
    try {
      const params = new URLSearchParams({ tip: 'MUSTERI', with_usage: 'true' });
      if (pasifGoster === '1') params.set('include_inactive', 'true');
      if (arama) params.set('q', arama);
      const res = await fetch(`${API}/api/cari?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      setMusteriler(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Müşteri yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [token, arama, pasifGoster, toast]);

  useEffect(() => { fetchMusteriler(); }, [fetchMusteriler]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      unvan: m.unvan || '',
      vergi_no: m.vergi_no || '',
      yetkili: m.yetkili || '',
      telefon: m.telefon || '',
      email: m.email || '',
      adres: m.adres || '',
      vade_gun: m.vade_gun ?? 30,
      kdv_orani: m.kdv_orani ?? 8,
      kategori: m.kategori || 'Genel',
      muhasebe_kodu: m.muhasebe_kodu || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.unvan.trim()) { toast.warning('Ünvan zorunludur'); return; }
    setSaving(true);
    try {
      const url = editing ? `${API}/api/cari/${editing.id}` : `${API}/api/cari`;
      const method = editing ? 'PUT' : 'POST';
      // POST tip=MUSTERI gerektirir; PUT'ta da gönderelim (tip değişmez ama no-op).
      const body = { ...form, tip: 'MUSTERI', vade_gun: parseInt(form.vade_gun) || 0, kdv_orani: parseFloat(form.kdv_orani) || 0 };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(editing ? 'Müşteri güncellendi' : 'Müşteri eklendi');
        setShowModal(false);
        setEditing(null);
        setForm(emptyForm);
        fetchMusteriler();
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('Müşteri kaydetme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const handlePasiflestir = async (m) => {
    if (!window.confirm(`"${m.unvan}" müşterisini pasifleştirmek istediğinize emin misiniz?\n\nKayıt pasife alınır; faturalar/alacaklar korunur.`)) return;
    try {
      const res = await fetch(`${API}/api/cari/${m.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${m.unvan}" pasife alındı`);
        fetchMusteriler();
      } else {
        // F13: açık alacağı varsa backend 400 + açıklayıcı mesaj döner.
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('Müşteri pasifleştirme hatası:', err);
      toast.error('Bağlantı hatası');
    }
  };

  if (loading && musteriler.length === 0) {
    return (
      <div>
        <Header title="Müşteriler" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Müşteriler" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Üst bar: başlık + yeni butonu */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>🧾 Müşteriler</h2>
          {canManage && (
            <button
              onClick={openNew}
              style={{ padding: '10px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
            >
              + Yeni Müşteri
            </button>
          )}
        </div>

        {/* Arama + pasif toggle */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Müşteri ara (ünvan, kod, vergi no)…"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            style={{ flex: 1, minWidth: '250px', padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: colors.textSecondary, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={pasifGoster === '1'}
              onChange={(e) => setPasifGoster(e.target.checked ? '1' : '0')}
            />
            Pasifleri göster
          </label>
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Kod</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Ünvan</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Vergi No</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Telefon</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Vade (Gün)</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>KDV %</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Durum</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Kullanım</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', width: '180px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {musteriler.map((m) => (
                  <tr key={m.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{m.kod || '-'}</td>
                    <td style={{ padding: '10px 12px' }}><strong>{m.unvan || '-'}</strong></td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px' }}>{m.vergi_no || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{m.telefon || '-'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{m.vade_gun ?? 30}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{m.kdv_orani ?? 8}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: 'bold',
                        background: m.aktif !== false ? '#d4edda' : '#f8d7da',
                        color: m.aktif !== false ? '#155724' : '#721c24',
                      }}>
                        {m.aktif !== false ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: colors.textSecondary }}>{m.usage_count ?? 0}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        {canManage && (
                          <button
                            onClick={() => openEdit(m)}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#1565c0', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            Düzenle
                          </button>
                        )}
                        {canManage && m.aktif !== false && (
                          <button
                            onClick={() => handlePasiflestir(m)}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#d32f2f', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            Pasifleştir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {musteriler.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                      {arama ? 'Arama kriterine uygun müşteri bulunamadı.' : 'Henüz müşteri eklenmemiş.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal: Yeni / Düzenle */}
        {showModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div style={{ background: colors.surface, padding: '30px', borderRadius: radius.lg, width: '520px', maxWidth: '92vw', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
                {editing ? 'Müşteri Düzenle' : 'Yeni Müşteri Ekle'}
              </h3>

              {/* M.3: kod düzenleme modunda görünür ama immutable. */}
              {editing && (
                <>
                  <label style={labelStyle}>Kod (değiştirilemez)</label>
                  <input style={{ ...inputStyle, background: '#f5f5f5', color: colors.textSecondary, cursor: 'not-allowed' }} value={editing.kod || ''} disabled readOnly />
                </>
              )}

              <label style={labelStyle}>Ünvan *</label>
              <input style={inputStyle} placeholder="Müşteri ünvanı" value={form.unvan} onChange={(e) => setForm({ ...form, unvan: e.target.value })} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Vergi No</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Vergi / TC No" value={form.vergi_no} onChange={(e) => setForm({ ...form, vergi_no: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Yetkili Kişi</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Ad Soyad" value={form.yetkili} onChange={(e) => setForm({ ...form, yetkili: e.target.value })} />
                </div>
              </div>

              <div style={{ height: '12px' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Telefon</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="05XX XXX XX XX" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>E-posta</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="ornek@firma.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>

              <div style={{ height: '12px' }} />

              <label style={labelStyle}>Adres</label>
              <textarea
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                placeholder="Adres bilgisi"
                value={form.adres}
                onChange={(e) => setForm({ ...form, adres: e.target.value })}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Vade (Gün)</label>
                  <input type="number" min="0" style={{ ...inputStyle, marginBottom: 0 }} value={form.vade_gun} onChange={(e) => setForm({ ...form, vade_gun: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>KDV %</label>
                  <input type="number" min="0" step="0.1" style={{ ...inputStyle, marginBottom: 0 }} value={form.kdv_orani} onChange={(e) => setForm({ ...form, kdv_orani: e.target.value })} />
                </div>
              </div>

              <div style={{ height: '12px' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Kategori</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Genel" value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Muhasebe Hesap Kodu</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Ör: 120.001" value={form.muhasebe_kodu} onChange={(e) => setForm({ ...form, muhasebe_kodu: e.target.value })} />
                </div>
              </div>

              <div style={{ height: '20px' }} />

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: 1, padding: '12px', border: 'none', borderRadius: radius.sm,
                    background: saving ? '#90caf9' : '#1565c0', color: 'white',
                    cursor: saving ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: '14px',
                  }}
                >
                  {saving ? 'Kaydediliyor…' : (editing ? 'Güncelle' : 'Kaydet')}
                </button>
                <button
                  onClick={() => { setShowModal(false); setEditing(null); setForm(emptyForm); }}
                  style={{
                    flex: 1, padding: '12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                    background: colors.background, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                  }}
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MusterilerPage;
